/**
 * PATH: packages/email-hub/src/feature/components/CalendarInviteCard.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-CALENDARINVITECARD-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/CalendarInviteCard.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/CalendarInviteCard.tsx:
 *     thin re-export shim that points back here.
 */

import { Calendar, MapPin, Clock, Users, ExternalLink, CheckCircle, XCircle, HelpCircle } from 'lucide-react'
import type { EmailCalendarEvent } from '../hooks/useEmails'
import { cn } from '../../_internal/lib/utils'

interface Props {
  event: EmailCalendarEvent
  className?: string
}

/**
 * Calendar Invite banner shown above the email body in EmailViewPage when
 * the message carries an underlying Microsoft Graph calendar event.
 *
 * Fields are pulled from the EmailCalendarEvent sidecar populated by
 * quotech-backend's email_sync_service. Renders in a degraded form when only
 * stub data is available (e.g. backend was unable to $expand=event because
 * the user's OAuth grant lacks Calendars.Read).
 */
export default function CalendarInviteCard({ event, className }: Props) {
  const isCancelled = event.meeting_message_type === 'meetingCancelled'
  const isResponse = [
    'meetingAccepted',
    'meetingTentativelyAccepted',
    'meetingDeclined',
  ].includes(event.meeting_message_type)
  const responseTone =
    event.meeting_message_type === 'meetingAccepted'
      ? 'accepted'
      : event.meeting_message_type === 'meetingDeclined'
      ? 'declined'
      : event.meeting_message_type === 'meetingTentativelyAccepted'
      ? 'tentative'
      : null

  const hasStartEnd = !!event.start_at
  const isStub = !hasStartEnd && (!event.organizer_email && !event.location)

  return (
    <section
      className={cn(
        'rounded-lg border bg-card overflow-hidden',
        isCancelled
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-primary/30 bg-primary/[0.04]',
        className,
      )}
      aria-label="Calendar invite"
    >
      <header
        className={cn(
          'flex items-center gap-2 px-4 py-2 text-xs font-medium uppercase tracking-wide',
          isCancelled ? 'text-destructive' : 'text-primary',
        )}
      >
        <Calendar className="h-3.5 w-3.5" />
        <span>
          {isCancelled
            ? 'Meeting cancelled'
            : isResponse
            ? `Meeting ${responseTone ?? 'response'}`
            : event.meeting_message_type === 'meetingForwardNotification'
            ? 'Meeting forwarded'
            : 'Calendar invite'}
        </span>
        {responseTone === 'accepted' && (
          <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
        )}
        {responseTone === 'declined' && (
          <XCircle className="h-3.5 w-3.5 text-destructive" />
        )}
        {responseTone === 'tentative' && (
          <HelpCircle className="h-3.5 w-3.5 text-amber-600" />
        )}
      </header>

      <div className="px-4 pb-4 pt-1 space-y-2">
        {event.subject && (
          <div className="text-base font-semibold text-foreground">
            {event.subject}
          </div>
        )}

        {hasStartEnd ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4 shrink-0" />
            <span>{formatRange(event)}</span>
          </div>
        ) : isStub ? (
          <div className="text-sm text-muted-foreground italic">
            Limited details - open the original message in Outlook to accept,
            decline, or see the full invite.
          </div>
        ) : null}

        {event.location && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>
        )}

        {event.organizer_email && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4 shrink-0" />
            <span className="truncate">
              Organised by{' '}
              <span className="text-foreground">
                {event.organizer_name || event.organizer_email}
              </span>
              {event.attendees && event.attendees.length > 0 && (
                <>
                  {' '}
                  · {event.attendees.length}{' '}
                  {event.attendees.length === 1 ? 'attendee' : 'attendees'}
                </>
              )}
            </span>
          </div>
        )}

        {event.is_online_meeting && event.online_meeting_join_url && (
          <a
            href={event.online_meeting_join_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            Join {prettyMeetingProvider(event.online_meeting_provider) || 'meeting'}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </section>
  )
}

function prettyMeetingProvider(provider: string): string {
  switch (provider) {
    case 'teamsForBusiness':
      return 'Teams meeting'
    case 'skypeForBusiness':
      return 'Skype meeting'
    case 'skypeForConsumer':
      return 'Skype meeting'
    case '':
      return ''
    default:
      return provider
  }
}

function formatRange(event: EmailCalendarEvent): string {
  if (!event.start_at) return ''
  const start = new Date(event.start_at)
  if (Number.isNaN(start.getTime())) return ''
  const end = event.end_at ? new Date(event.end_at) : null

  const dateOpts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: start.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  }
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  }

  const startDate = start.toLocaleDateString(undefined, dateOpts)
  const startTime = event.is_all_day ? 'all day' : start.toLocaleTimeString(undefined, timeOpts)

  if (event.is_all_day) {
    return `${startDate} · all day`
  }

  if (!end || Number.isNaN(end.getTime())) {
    return `${startDate} · ${startTime}`
  }

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate()

  const endTime = end.toLocaleTimeString(undefined, timeOpts)
  if (sameDay) {
    return `${startDate} · ${startTime} – ${endTime}`
  }
  const endDate = end.toLocaleDateString(undefined, dateOpts)
  return `${startDate} ${startTime} → ${endDate} ${endTime}`
}
